/**
 * Time Window Timeline Component
 *
 * Visualizes time-windows on a timeline and highlights overlaps.
 */

import { useMemo } from 'react';
import { Alert } from 'react-bootstrap';

interface TimeWindow {
  id: number | string;
  label: string;
  start: Date;
  end: Date;
  entity1?: number; // For clinic-device: clinic_id, for study-clinic: study_id, for study-device: study_id
  entity2?: number; // For clinic-device: device_id, for study-clinic: clinic_id, for study-device: device_id
}

interface TimeWindowTimelineProps {
  windows: TimeWindow[];
  highlightOverlaps?: boolean;
  height?: number;
}

export default function TimeWindowTimeline({
  windows,
  highlightOverlaps = true,
  height = 200,
}: TimeWindowTimelineProps) {
  // Calculate timeline bounds
  const { minDate, maxDate, overlaps } = useMemo(() => {
    if (windows.length === 0) {
      return { minDate: new Date(), maxDate: new Date(), overlaps: [] };
    }

    const dates = windows.flatMap((w) => [w.start, w.end]);
    const min = new Date(Math.min(...dates.map((d) => d.getTime())));
    const max = new Date(Math.max(...dates.map((d) => d.getTime())));

    // Detect overlaps
    // Only detect overlaps when windows belong to the same entity pair (e.g., same clinic-device combination)
    // and their time ranges overlap
    const overlappingPairs: Array<[number | string, number | string]> = [];
    if (highlightOverlaps) {
      for (let i = 0; i < windows.length; i++) {
        for (let j = i + 1; j < windows.length; j++) {
          const w1 = windows[i];
          const w2 = windows[j];
          // Only check overlaps if both entity1 and entity2 match (same clinic-device pair, etc.)
          const sameEntityPair =
            w1.entity1 !== undefined && w2.entity1 !== undefined && w1.entity1 === w2.entity1 &&
            w1.entity2 !== undefined && w2.entity2 !== undefined && w1.entity2 === w2.entity2;

          // Check if windows overlap: start1 <= end2 AND start2 <= end1
          // Only if they belong to the same entity pair
          if (sameEntityPair && w1.start <= w2.end && w2.start <= w1.end) {
            overlappingPairs.push([w1.id, w2.id]);
          }
        }
      }
    }

    return { minDate: min, maxDate: max, overlaps: overlappingPairs };
  }, [windows, highlightOverlaps]);

  const totalDuration = maxDate.getTime() - minDate.getTime();

  const getPosition = (date: Date) => {
    if (totalDuration === 0) return 0;
    return ((date.getTime() - minDate.getTime()) / totalDuration) * 100;
  };

  const isOverlapping = (id: number | string) => {
    return overlaps.some((pair) => pair.includes(id));
  };

  if (windows.length === 0) {
    return <Alert variant="info">No time-windows to display</Alert>;
  }

  return (
    <div>
      {overlaps.length > 0 && (
        <Alert variant="warning" className="mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Warning:</strong> {overlaps.length} overlap{overlaps.length !== 1 ? 's' : ''} detected
          in time-windows. This may cause conflicts.
        </Alert>
      )}

      <div className="mb-2 d-flex justify-content-between text-muted" style={{ fontSize: '0.85em' }}>
        <span>{minDate.toLocaleDateString()}</span>
        <span>{maxDate.toLocaleDateString()}</span>
      </div>

      <div
        style={{
          position: 'relative',
          height: `${height}px`,
          background: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
        }}
      >
        {/* Timeline axis */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '2px',
            background: '#dee2e6',
          }}
        />

        {/* Time windows */}
        {windows.map((window, idx) => {
          const left = getPosition(window.start);
          const width = getPosition(window.end) - left;
          const isOverlap = isOverlapping(window.id);

          return (
            <div
              key={window.id}
              style={{
                position: 'absolute',
                left: `${left}%`,
                width: `${width}%`,
                top: `${(idx % 3) * 30 + 10}px`,
                height: '25px',
                background: isOverlap ? '#dc3545' : '#0d6efd',
                borderRadius: '4px',
                border: '2px solid ' + (isOverlap ? '#bd2130' : '#0a58ca'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.75em',
                fontWeight: 'bold',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                padding: '0 4px',
                cursor: 'pointer',
              }}
              title={`${window.label}\n${window.start.toLocaleString()} - ${window.end.toLocaleString()}`}
            >
              {window.label}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-2 d-flex gap-3" style={{ fontSize: '0.85em' }}>
        <div className="d-flex align-items-center">
          <div
            style={{
              width: '20px',
              height: '12px',
              background: '#0d6efd',
              border: '1px solid #0a58ca',
              marginRight: '6px',
            }}
          />
          <span>Valid time-window</span>
        </div>
        {overlaps.length > 0 && (
          <div className="d-flex align-items-center">
            <div
              style={{
                width: '20px',
                height: '12px',
                background: '#dc3545',
                border: '1px solid #bd2130',
                marginRight: '6px',
              }}
            />
            <span>Overlapping time-window</span>
          </div>
        )}
      </div>
    </div>
  );
}
